/**
 * Deutsche Sätze zu den Fehlercodes der Stellplatz-Belegung (`ParkingBlockService`,
 * `ParkingMappedReservationService`, `ParkingOccupancyService`, Vorschläge). Der Server schreibt
 * manche nur englisch oder ohne Umlaute; unbekannte Codes bleiben über `problemText` sichtbar.
 */
import { problemText, type ApiError } from '@/shared/api';

const PARKING_PROBLEMS: Record<string, string> = {
  parking_object_not_found: 'Dieses Objekt gibt es nicht mehr oder es gehört nicht zu dir.',
  object_not_found: 'Dieses Objekt gibt es nicht mehr oder es gehört nicht zu dir.',
  parking_object_access_denied:
    'Stellplätze sieht und verwaltet nur, wer alle Apartments dieses Objekts sehen darf.',
  parking_spot_not_found: 'Der Stellplatz gehört nicht (mehr) zu diesem Objekt.',
  parking_block_not_found: 'Diese Belegung gibt es nicht mehr – der Stand wird neu geladen.',
  parking_currency_mismatch: 'Stellplatz und Buchung rechnen in verschiedenen Währungen.',
  parking_occupancy_range_invalid: 'Das Ende des Zeitraums liegt vor dem Beginn.',
  parking_occupancy_range_too_long: 'Es lassen sich höchstens 180 Tage auf einmal zeigen.',
  parking_block_range_invalid: 'Das Ende muss nach dem Beginn liegen.',
  parking_mapping_range_invalid: 'Das Ende muss nach dem Beginn liegen.',
  parking_block_no_spots: 'Es ist kein Stellplatz ausgewählt.',
  parking_mapping_no_spots: 'Es ist kein Stellplatz ausgewählt.',
  parking_mapping_booking_not_found:
    'Die Buchung gehört nicht zu diesem Objekt oder ist storniert.',
  parking_mapping_spot_not_assigned:
    'Der Stellplatz ist dem Apartment dieser Buchung nicht zugewiesen.',
  parking_reservation_paid:
    'Diese Reservierung ist bezahlt – sie wird über die Gutschrift ihrer Rechnung aufgehoben, dabei wird der Platz frei.',
  parking_reservation_conflict: 'Der Stellplatz ist in diesem Zeitraum schon belegt.',
  parking_license_plate_too_long: 'Das Kennzeichen ist zu lang.',
  parking_payment_unavailable:
    'Ohne eingerichtete Bezahlung könnte der Gast diesen Platz nie bezahlen – für eine kostenlose Belegung den Zeitraum sperren.',
};

/** Fehlendes Recht am Objekt: ein neuer Versuch brächte dieselbe Antwort. */
export const parkingAccessDenied = (problem: ApiError | null) =>
  problem?.title === 'parking_object_access_denied';

export const parkingProblemText = (problem: ApiError | null, fallback: string) =>
  problemText(problem, fallback, { known: PARKING_PROBLEMS });

/**
 * Satz zu einer gescheiterten Änderung. Ohne Antwort ist offen, ob sie angekommen ist – der Stand
 * wird neu geladen, statt ein „nicht gespeichert“ zu behaupten.
 */
export function parkingChangeProblem(problem: ApiError, fallback: string, what: string) {
  return problem.status === 0
    ? `Keine Antwort vom Server – ob ${what}, ist offen. Der Stand wird neu geladen.`
    : parkingProblemText(problem, fallback);
}
